import CryptoJS from 'crypto-js';

export class AESUtil {
    constructor() {
        this._keySize = 256; // Key size in bits
        this._ivSize = 128; // IV size in bits
        this._iterationCount = 1989; // PBKDF2 iterations
        this._secretKey = process.env.REACT_APP_SECRET_KEY; // Passphrase
    }

    // Helper function to generate a random Uint8Array
    generateRandomBytes(length) {
        let array = new Uint8Array(length);
        window.crypto.getRandomValues(array);
        return array;
    }

    // Generate a key using PBKDF2
    generateKey(salt, passPhrase) {
        let key = CryptoJS.PBKDF2(passPhrase, CryptoJS.lib.WordArray.create(salt), {
            keySize: this._keySize / 32, // Convert bits to bytes
            iterations: this._iterationCount,
        });
        return new Uint8Array(key.words.map((word) => [
            (word >> 24) & 0xff,
            (word >> 16) & 0xff,
            (word >> 8) & 0xff,
            word & 0xff,
        ]).flat());
    }

    arrayBufferToBase64(buffer) {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    // Utility function to convert Base64 to Uint8Array
    base64ToUint8Array(base64) {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
    }

    // Encrypt function
    encrypt(plainText) {
        const passPhrase = this._secretKey;

        // Generate random IV and salt
        const iv = crypto.getRandomValues(new Uint8Array(this._ivSize / 8)); // 16 bytes
        const salt = crypto.getRandomValues(new Uint8Array(this._keySize / 8)); // 32 bytes

        // Generate encryption key from salt and passphrase
        const key = CryptoJS.PBKDF2(passPhrase, CryptoJS.lib.WordArray.create(salt), {
            keySize: this._keySize / 32, // 256 bits
            iterations: 1989,
        });

        // Encrypt the plaintext
        const cipher = CryptoJS.AES.encrypt(plainText, key, {
            iv: CryptoJS.lib.WordArray.create(iv),
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        // Extract ciphertext as Uint8Array
        const ciphertext = Uint8Array.from(
            cipher.ciphertext.words.flatMap((word) => [
                (word >> 24) & 0xff,
                (word >> 16) & 0xff,
                (word >> 8) & 0xff,
                word & 0xff,
            ])
        );

        // Combine salt, IV, and ciphertext
        const combined = new Uint8Array(salt.length + iv.length + ciphertext.length);
        combined.set(salt, 0);
        combined.set(iv, salt.length);
        combined.set(ciphertext, salt.length + iv.length);

        // Convert combined data to Base64
        return this.arrayBufferToBase64(combined.buffer);
    }

    // Decrypt function
    decrypt(encryptedData) {
        // Decode Base64 data into a Uint8Array
        const combined = this.base64ToUint8Array(encryptedData);

        const saltLength = this._keySize / 8; // 32 bytes for 256-bit key
        const ivLength = this._ivSize / 8;   // 16 bytes for 128-bit IV

        // Extract salt, IV, and ciphertext
        const salt = combined.slice(0, saltLength);
        const iv = combined.slice(saltLength, saltLength + ivLength);
        const ciphertext = combined.slice(saltLength + ivLength);

        // Generate decryption key using PBKDF2
        const key = CryptoJS.PBKDF2(this._secretKey, CryptoJS.lib.WordArray.create(salt), {
            keySize: this._keySize / 32, // 256 bits
            iterations: 1989,
        });

        // Create a CipherParams object for the ciphertext
        const cipherParams = CryptoJS.lib.CipherParams.create({
            ciphertext: CryptoJS.lib.WordArray.create(ciphertext),
        });

        // Decrypt the ciphertext using AES-CBC
        const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
            iv: CryptoJS.lib.WordArray.create(iv),
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        // Convert decrypted data to a UTF-8 string
        return decrypted.toString(CryptoJS.enc.Utf8);
    }

}

export const aesUtil = new AESUtil();
